import { Directive, ElementRef, inject, input, OnDestroy, OnInit, signal, ViewContainerRef } from '@angular/core';
// eslint-disable-next-line mib/no-cdk-overlay -- tour steps anchor to arbitrary app elements; mib.ui has no anchored-popover API for this
import { ConnectedPosition, Overlay, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import type { TourAnchorDirective as CoreTourAnchor } from 'ngx-ui-tour-core';
import { TourState } from 'ngx-ui-tour-core';
import { Subscription } from 'rxjs';
import { Tour } from './tour';
import { TourStepTemplateRegistry } from './tour-step-template-registry';
import type { IStepOption } from './step-option';

/** Marks an element as a tour stop and opens the step next to it in a CDK overlay. */
@Directive({
  // eslint-disable-next-line @angular-eslint/directive-selector -- the tour library's attribute, used across templates
  selector: '[tourAnchor]',
  host: {
    '[class.touranchor--is-active]': 'isActive()',
  },
})
export class TourAnchorOverlay implements OnInit, OnDestroy, CoreTourAnchor {
  readonly tourAnchor = input<string>();
  readonly isActive = signal(false);
  readonly element = inject(ElementRef);

  private readonly overlay = inject(Overlay);
  private readonly viewContainer = inject(ViewContainerRef);
  private readonly tourService = inject(Tour);
  private readonly templateService = inject(TourStepTemplateRegistry);
  private overlayRef: OverlayRef | null = null;
  private subscriptions = new Subscription();

  ngOnInit(): void {
    this.tourService.register(this.tourAnchor()!, this);
  }

  ngOnDestroy(): void {
    this.tourService.unregister(this.tourAnchor()!);
    this.close();
  }

  showTourStep(step: IStepOption): void {
    const panel = this.templateService.panel();
    if (!panel) return;
    this.close();
    this.isActive.set(true);

    const positionStrategy = this.overlay.position().flexibleConnectedTo(this.element).withPositions(this.positions(step)).withLockedPosition();
    const overlayRef = this.overlay.create({
      positionStrategy,
      scrollStrategy: this.overlay.scrollStrategies.reposition(),
      hasBackdrop: !!step.closeOnOutsideClick,
      backdropClass: 'cdk-overlay-transparent-backdrop',
      panelClass: ['tour-step', ...(step.showArrow ? ['arrow'] : []), ...(step.placement?.horizontal ? ['horizontal'] : [])],
    });
    this.overlayRef = overlayRef;

    // The arrow follows the side the step actually opened on, flips included.
    this.subscriptions.add(
      positionStrategy.positionChanges.subscribe(({ connectionPair }) => {
        overlayRef.removePanelClass(['tour-step--above', 'tour-step--below', 'tour-step--before', 'tour-step--after']);
        overlayRef.addPanelClass([
          connectionPair.overlayY === 'bottom' ? 'tour-step--above' : 'tour-step--below',
          connectionPair.overlayX === 'end' ? 'tour-step--before' : 'tour-step--after',
        ]);
      }),
    );
    overlayRef.attach(new TemplatePortal(panel, this.viewContainer, { step }));
    overlayRef.overlayElement.querySelector<HTMLElement>('.tour-step-panel')?.focus({ preventScroll: true });

    // Being the top overlay with a key listener keeps Escape from also closing a dialog under the step.
    this.subscriptions.add(overlayRef.keydownEvents().subscribe());

    // Clicking outside ends the tour, as the Material menu it replaces did.
    this.subscriptions.add(
      overlayRef.backdropClick().subscribe(() => {
        if (this.tourService.getStatus() !== TourState.OFF) this.tourService.end();
      }),
    );
  }

  hideTourStep(): void {
    this.isActive.set(false);
    this.close();
  }

  private close(): void {
    this.subscriptions.unsubscribe();
    this.subscriptions = new Subscription();
    this.overlayRef?.dispose();
    this.overlayRef = null;
  }

  /** The Material menu's placement rules: preferred position first, then its flips. */
  private positions(step: IStepOption): ConnectedPosition[] {
    const before = step.placement?.xPosition === 'before';
    const above = step.placement?.yPosition === 'above';
    const offset = step.backdropConfig?.offset ?? 0;

    if (step.placement?.horizontal) {
      const originX = before ? 'start' : 'end';
      const overlayX = before ? 'end' : 'start';
      const overlayY = above ? 'bottom' : 'top';
      return [
        { originX, originY: overlayY, overlayX, overlayY, offsetX: offset, offsetY: -offset },
        { originX: overlayX, originY: overlayY, overlayX: originX, overlayY, offsetX: -offset, offsetY: -offset },
      ];
    }

    const originX = before ? 'end' : 'start';
    const flippedX = before ? 'start' : 'end';
    const overlayY = above ? 'bottom' : 'top';
    const flippedY = above ? 'top' : 'bottom';
    return [
      { originX, originY: flippedY, overlayX: originX, overlayY, offsetX: -offset, offsetY: offset },
      { originX, originY: overlayY, overlayX: originX, overlayY: flippedY, offsetX: -offset, offsetY: -offset },
      { originX: flippedX, originY: overlayY, overlayX: flippedX, overlayY: flippedY, offsetX: offset, offsetY: -offset },
    ];
  }
}
